import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';

import CorpsDevicesToolbar from './CorpsDevicesToolbar';
import DevicesTable from './DevicesTable';
import AddDeviceDialog from './AddDeviceDialog';
import MeterReadingsImportDialog from './MeterReadingsImportDialog';

import {
  $devicesByCorps,
  $devicesTotal,
  $devicesPagination,
  fetchDevicesByCorpsFx,
  dateChanged,
  $devicesDate,
  updateDeviceFx,
} from '../../store/devices';
import { $canDeleteComplex, $isSettlementTenant } from '../../store/auth';
import { getObjectKindLabels } from '../../utils/complexKind';

/**
 * Таблица приборов внутри карточки дома (в т.ч. дом без корпусов).
 */
export default function BuildingDevicesSection({ buildingId, corpsId, houseMode = false }) {
  const [devicesMap, totalMap, pagMap, date, canManage, isSettlementTenant] = useUnit([
    $devicesByCorps,
    $devicesTotal,
    $devicesPagination,
    $devicesDate,
    $canDeleteComplex,
    $isSettlementTenant,
  ]);
  const labels = useMemo(
    () => getObjectKindLabels(isSettlementTenant),
    [isSettlementTenant]
  );
  const updateDevice = useUnit(updateDeviceFx);
  const loading = useUnit(fetchDevicesByCorpsFx.pending);

  const [page, setPage] = useState(1);
  const pageSize = pagMap[corpsId || '']?.pageSize || 10;
  const items = useMemo(
    () => devicesMap[String(corpsId)] || [],
    [devicesMap, corpsId]
  );
  const total = totalMap[String(corpsId)] || 0;

  const [searchQuery, setSearchQuery] = useState('');
  const [filtersDraft, setFiltersDraft] = useState({
    status: '',
    system_type: '',
    manufacturer: '',
    serial_number: '',
    room_number: '',
    suffix: '',
  });
  const [filters, setFilters] = useState(filtersDraft);
  const [sortBy, setSortBy] = useState(houseMode ? 'system_type' : 'room_number');
  const [sortDir, setSortDir] = useState('asc');
  const [openAdd, setOpenAdd] = useState(false);
  const [openImport, setOpenImport] = useState(false);

  const hasDevices = total > 0;

  const reload = useCallback(() => {
    if (!corpsId) return;
    fetchDevicesByCorpsFx({
      corpsId: String(corpsId),
      date,
      page,
      pageSize,
      search: searchQuery || undefined,
      filters,
      sort: { sort_by: sortBy, sort_dir: sortDir },
    });
  }, [corpsId, date, page, pageSize, searchQuery, filters, sortBy, sortDir]);

  useEffect(() => {
    const t = setTimeout(() => setFilters(filtersDraft), 300);
    return () => clearTimeout(t);
  }, [filtersDraft]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleDeviceUpdate = useCallback(
    (device, changes) => {
      if (!device) return Promise.resolve();
      return updateDevice({
        id: String(device.id),
        corpsId: String(device.corps_id ?? corpsId ?? ''),
        data: changes,
      });
    },
    [updateDevice, corpsId]
  );

  if (!corpsId) {
    return (
      <div className="rc-empty" style={{ padding: 32, textAlign: 'center', color: '#64748B' }}>
        Готовим дом…
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        {canManage && hasDevices ? (
          <button className="rc-btn" type="button" onClick={() => setOpenImport(true)}>
            Импорт показаний
          </button>
        ) : null}
        {canManage ? (
          <button className="rc-btn" type="button" onClick={() => setOpenAdd(true)}>
            {houseMode ? 'Добавить прибор' : 'Добавить устройство'}
          </button>
        ) : null}
      </div>

      <CorpsDevicesToolbar
        date={date || ''}
        onDateChange={(d) => dateChanged(d)}
        corpsTabs={[]}
        onSearch={(value) => {
          setSearchQuery(value || '');
          setPage(1);
        }}
        hideExport
        searchValue={searchQuery}
      />

      <DevicesTable
        rows={items}
        total={total}
        page={page}
        pageSize={pageSize}
        loading={loading}
        filters={filtersDraft}
        sortBy={sortBy}
        sortDir={sortDir}
        roomNumberLabel={labels.premiseNumber}
        houseMode={houseMode}
        onFiltersChange={(next) => {
          setFiltersDraft(next);
          setPage(1);
        }}
        onSortChange={(field) => {
          const nextDir = sortBy === field && sortDir === 'asc' ? 'desc' : 'asc';
          setSortBy(field);
          setSortDir(nextDir);
          setPage(1);
        }}
        onPageChange={(p) => setPage(p)}
        onDeviceUpdate={handleDeviceUpdate}
        onDeviceReplaced={reload}
      />

      {canManage ? (
        <AddDeviceDialog
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          corpsId={String(corpsId)}
          buildingId={String(buildingId || '')}
          houseMode={houseMode}
          onSuccess={() => {
            setPage(1);
            reload();
          }}
        />
      ) : null}

      {canManage ? (
        <MeterReadingsImportDialog
          open={openImport}
          onClose={() => setOpenImport(false)}
          onImported={reload}
          buildingId={buildingId}
        />
      ) : null}
    </div>
  );
}
